'use client'
import React, { useCallback } from 'react'
import { faPlus, faSearch, faTimes } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Box,
  Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import Buttons from 'src/components/era-package/era/Buttons'
import Button from 'src/components/era-package/forms/Button'
import EditModal from 'src/components/era-package/forms/EditModal'
import { useDataTable } from 'src/utils/era-package/dataTable'
import HtsCodeList from "../../../views/abi/htscode/HtsCodeList"
import { useRowEdit } from './hooks/useRowEdit'
import { useRowDrag } from './hooks/useRowDrag'

const DataTableSection = ({
  title,
  columns,
  fields, name, error,
  optionsMap,
  data = [],
  onDataChange,
  maxRows,
  setHasUnsaved,
  disableEdit = false,
  disableAdd = false,
  onRowClick, // New prop for custom row click handler
  customColumn,
  showHtsLookup = false,
  htsRowClick,
    editMode = 'modal', // 'modal', 'row', or 'none'
  draggable = false,
  nestedTableConfig,
  showIconActions = true, // when true: show edit/delete icons per row instead of click-row-to-edit
  uniqueFields = [], // fields that must be unique within the table
}) => {
  const theme = useTheme()

  const {
    isModalOpen,
    editingRow,
    editingIndex,
    generatedColumns,
    handleAddRow,
    handleEditRow,
    handleSave,
    handleDelete,
    handleCloseModal,
    openHtsLookupDialog,
    setOpenHtsLookupDialog,
  } = useDataTable({
    data,
    onDataChange,
    fields,
    columns,
    optionsMap,
    setHasUnsaved,
  })

  const {
    tableData, tableColumns,
    isInteractionLocked,
    isRowEditMode, rowEditingIndex,
    handleAddClick, handleRowClick,
    renderEditableCell
  } = useRowEdit(
        fields, optionsMap, data,
    customColumn, generatedColumns, null,
    disableEdit, editMode, handleEditRow,
    disableAdd, handleAddRow,
    setHasUnsaved, onDataChange, onRowClick,
    showIconActions,
    uniqueFields
  )

  const handleReorder = useCallback((nextRows) => {
    onDataChange(nextRows)
    if (setHasUnsaved) {
      setHasUnsaved(true)
    }
  }, [onDataChange, setHasUnsaved])

  const {
    dragColumn,
    draggingIndex,
    dragOverIndex,
    setRowElementRef,
    handleRowDragOver,
    setDragOverIndex,
    handleRowDrop,
  } = useRowDrag({
    draggable,
    isInteractionLocked,
    rows: tableData,
    onReorder: handleReorder,
  })

  const finalTableColumns = [...dragColumn, ...tableColumns]

  const table = useReactTable({
    data: tableData,
    columns: finalTableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const isRowClickable = isRowEditMode
    ? !disableEdit && rowEditingIndex === null
    : !disableEdit || !!onRowClick

  return (
    <Stack spacing={2}>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      cursor: header.column.getCanSort() && !isInteractionLocked ? 'pointer' : 'default',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: header.column.getCanSort() && !isInteractionLocked
                          ? theme.palette.action.hover
                          : 'transparent',
                      },
                    }}
                    onClick={isInteractionLocked ? undefined : header.column.getToggleSortingHandler()}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() && (
                        <Box sx={{ ml: 0.5, fontSize: '0.75rem' }}>
                          {header.column.getIsSorted() === 'asc' ? '↑' : '↓'}
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row, index) => (
              <TableRow
                key={row.id}
                ref={(element) => setRowElementRef(row.index, element)}
                hover={isRowClickable}
                onDragOver={draggable ? handleRowDragOver : undefined}
                onDragEnter={draggable ? () => setDragOverIndex(row.index) : undefined}
                onDrop={draggable ? (event) => handleRowDrop(event, row.index) : undefined}
                onClick={() => {
                  if (draggingIndex !== null) return
                  handleRowClick(row, index)
                }}
                sx={{
                  cursor: isRowClickable ? 'pointer' : 'default',
                  opacity: draggingIndex === row.index ? 0.6 : 1,
                  boxShadow: dragOverIndex === row.index && draggingIndex !== null
                    ? `inset 0 0 0 2px ${theme.palette.primary.light}`
                    : 'none',
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {renderEditableCell(cell, index)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {tableData.length === 0 && (
              <TableRow>
                <TableCell colSpan={finalTableColumns.length} align="center">
                  <Typography variant="body2" color={theme.palette.info.main}>
                    No {title} records{!disableEdit ? '. Click "Add" to add one.' : '.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Buttons
        other={showHtsLookup ? <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            onClick={() => setOpenHtsLookupDialog(true)}
            size="medium"
            startIcon={<FontAwesomeIcon icon={faSearch} width={12} />}
            title={`HTS Lookup`}
          >
            HTS Lookup
          </Button>
        </Box> : error}
        advance={
          maxRows && tableData.length >= maxRows
            ?
            <Typography variant="body2" color={theme.palette.info.main}>
              Maximum {maxRows} {maxRows === 1 ? 'record' : 'records'} allowed
            </Typography>
            :
            (!disableEdit && !disableAdd) &&
            <Button
              size="medium"
              minWidth={80}
              startIcon={<FontAwesomeIcon icon={faPlus} width={12} />}
              onClick={handleAddClick}
              disabled={isInteractionLocked}
              title={`Add a new ${title} record`}
            >
              Add
            </Button>
        }
      />

      <Dialog open={openHtsLookupDialog} onClose={() => setOpenHtsLookupDialog(false)} fullWidth={true} maxWidth={'80dvw'}>
        <DialogTitle
          id="hts-lookup-dialog-title"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          HTS Lookup
          <IconButton
            aria-label="Close HTS Lookup"
            onClick={() => setOpenHtsLookupDialog(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
            title="Close HTS Lookup"
          >
            <Box component={FontAwesomeIcon} icon={faTimes} sx={{ width: '1em' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          id="hts-lookup-dialog-content"
          sx={{
            px: {
              xs: 2,
              sm: 4,
            },
          }}
        >
          <Box>
            <HtsCodeList view={true} onRowClick={htsRowClick} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ pt: 3, justifyContent: 'center' }}>
          <Button onClick={() => setOpenHtsLookupDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <EditModal
        title={title}
        open={!isRowEditMode && isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        onDelete={handleDelete}
        fields={fields}
        optionsMap={optionsMap}
        initialData={editingRow}
        isEditing={editingIndex !== null}
        index={editingIndex}
        name={name}
        nestedTableConfig={nestedTableConfig}
      />

    </Stack>
  )
}

export default DataTableSection
